import {
  collection,
  setDoc,
  doc,
  Timestamp,
  onSnapshot,
} from 'firebase/firestore'

import {
  useCurrentUser,
  useFirestore,
  useCollection,
  useDocument,
} from 'vuefire'
import type { FirestoreError } from 'firebase/firestore'

import { UserModelFactory } from 'monocle-models'
import {
  parseAdminUserDocument,
  type AdminUserListItem,
} from '~/utils/adminUserDocument'

const { userConverter, UserDBModel } = new UserModelFactory(Timestamp)

import { z } from 'zod'

export default function () {
  const firestore = useFirestore()
  const userCollection = collection(firestore, 'users').withConverter(
    userConverter,
  )

  const authUser = useCurrentUser()

  // const userId = user.value?.uid
  const userId = computed(() => authUser.value?.uid)

  const useAllUsers = (enabled?: Ref<boolean>) => {
    if (!enabled) {
      return useCollection(userCollection)
    }
    const enabledQuery = computed(() => (enabled.value ? userCollection : null))
    return useCollection(enabledQuery)
  }

  const useAdminUsersLive = () => {
    const users = ref<AdminUserListItem[]>([])
    const isLoading = ref(true)
    const error = ref<FirestoreError | null>(null)
    const adminUserCollection = collection(firestore, 'users')
    const reportedCorruptUserIds = new Set<string>()

    const unsubscribe = onSnapshot(
      adminUserCollection,
      (snapshot) => {
        users.value = snapshot.docs.map((document) => {
          const user = parseAdminUserDocument(document.id, document.data())

          if (user.isCorrupt && !reportedCorruptUserIds.has(user.id)) {
            reportedCorruptUserIds.add(user.id)
            console.error('Corrupt Firestore user document', {
              userId: user.id,
              validationIssues: user.validationIssues,
            })
          } else if (!user.isCorrupt) {
            reportedCorruptUserIds.delete(user.id)
          }

          return user
        })
        isLoading.value = false
        error.value = null
      },
      (snapshotError) => {
        error.value = snapshotError
        isLoading.value = false
      },
    )

    onScopeDispose(() => {
      unsubscribe()
    })

    return {
      users,
      isLoading,
      error,
    }
  }

  const getUserById = async (userId: string) => {
    const userRef = doc(userCollection, userId)
    return useDocument(userRef)
  }

  const updateUser = async (user: z.infer<typeof UserDBModel>) => {
    await setDoc(doc(userCollection, user.id), user)
  }

  const userRef = computed(() => {
    if (!userId.value) {
      return null
    }
    return doc(userCollection, userId.value)
  })
  const currentUser = useDocument(userRef)

  const { isUserNearQuota, isUserOverQuota } = useQuota()

  const isCurrentUserOverQuota = computed(() => {
    const user = currentUser.value
    return user ? isUserOverQuota(user) : false
  })
  const isCurrentUserNearQuota = computed(() => {
    const user = currentUser.value
    return user ? isUserNearQuota(user) : false
  })

  const isCurrentUserInFreeTrial = computed(() => {
    const user = currentUser.value
    return user ? UserModelFactory.isFreeTrialActive(user, new Date()) : false
  })

  const isCurrentUserFreeTrialNearlyOver = computed(() => {
    const user = currentUser.value
    return user
      ? UserModelFactory.isFreeTrialNearlyOver(user, new Date())
      : false
  })

  const currentUserHasVerifiedPartnerLink = useState<boolean | null>(
    'currentUserHasVerifiedPartnerLink',
    () => null,
  )
  const currentUserPartnerLinkFetchPendingFor = useState<string | null>(
    'currentUserPartnerLinkFetchPendingFor',
    () => null,
  )
  const currentUserPartnerLinkStateFor = useState<string | null>(
    'currentUserPartnerLinkStateFor',
    () => null,
  )

  const refreshCurrentUserVerifiedPartnerLink = async () => {
    const userId = currentUser.value?.id
    if (!userId) {
      currentUserHasVerifiedPartnerLink.value = null
      currentUserPartnerLinkFetchPendingFor.value = null
      currentUserPartnerLinkStateFor.value = null
      return
    }

    if (currentUserPartnerLinkStateFor.value !== userId) {
      currentUserHasVerifiedPartnerLink.value = null
      currentUserPartnerLinkStateFor.value = userId
    }

    if (currentUserPartnerLinkFetchPendingFor.value === userId) {
      return
    }

    currentUserPartnerLinkFetchPendingFor.value = userId
    try {
      const res = await $galleryApi<{ hasVerifiedPartnerLink: boolean }>(
        '/api/user/has-verified-partner-link',
      )
      if (currentUser.value?.id === userId) {
        currentUserHasVerifiedPartnerLink.value = res.hasVerifiedPartnerLink
      }
    } catch {
      if (currentUser.value?.id === userId) {
        currentUserHasVerifiedPartnerLink.value = false
      }
    } finally {
      if (currentUserPartnerLinkFetchPendingFor.value === userId) {
        currentUserPartnerLinkFetchPendingFor.value = null
      }
    }
  }

  watch(
    currentUser,
    () => {
      void refreshCurrentUserVerifiedPartnerLink()
    },
    { immediate: true },
  )

  const shouldCurrentUserSeeFreeTrialPrompt = computed(() => {
    return (
      isCurrentUserFreeTrialNearlyOver.value &&
      currentUserHasVerifiedPartnerLink.value === false
    )
  })

  return {
    isCurrentUserInFreeTrial,
    isCurrentUserFreeTrialNearlyOver,
    currentUserHasVerifiedPartnerLink,
    shouldCurrentUserSeeFreeTrialPrompt,
    isCurrentUserNearQuota,
    isCurrentUserOverQuota,
    updateUser,
    getUserById,
    useAllUsers,
    currentUser,
    useAdminUsersLive,
  }
}
